"use client";

import { useMemo, useState } from "react";
import { HairlineDivider } from "@/components/ui/hairline-divider";
import type { Line } from "@/lib/schemas/lines";
import type { ProductListItem } from "@/lib/schemas/products";
import { cn } from "@/lib/utils";
import { StyleProductCard } from "./style-product-card";

type SortKey = "new" | "asc" | "desc";

const SORTS: [SortKey, string][] = [
	["new", "Newest"],
	["asc", "Price ↑"],
	["desc", "Price ↓"],
];

function sortProducts(
	products: ProductListItem[],
	sort: SortKey,
): ProductListItem[] {
	if (sort === "asc") return [...products].sort((a, b) => a.price - b.price);
	if (sort === "desc") return [...products].sort((a, b) => b.price - a.price);
	// "new" preserves the API's natural order (newest-first by contract).
	return products;
}

type ProductGridProps = {
	products: ProductListItem[];
	// All lines, in canonical order — used to resolve each product's palette
	// (DropReel placeholder) and its "LINE 0X · NAME" label.
	lines: Line[];
	// When true, each card shows its line label (cross-line surfaces like
	// Collections). When false, the label is omitted (single-line Styles PLP).
	showLine: boolean;
	// Show the Newest / Price sort control. Hidden by default — the PLPs keep
	// the API's curated order.
	showSort?: boolean;
	// Show the "N shirts" count line above the grid. On by default (it reads as
	// a result count on the search + category pages); the single-line Styles PLP
	// turns it off.
	showCount?: boolean;
	emptyMessage?: string;
};

// Shared sort bar + dense merch grid. Client-side because sort is interactive
// state; the catalog itself is server-fetched and passed in. Audience/gender
// filtering (Men/Women/Kids) happens server-side via tag filters on the
// category + search pages (ADR 0015); this grid just renders the already-
// filtered set, so only the client-side sort lives here.
export function ProductGrid({
	products,
	lines,
	showLine,
	showSort = false,
	showCount = true,
	emptyMessage = "No shirts here yet — check back soon.",
}: ProductGridProps) {
	const [sort, setSort] = useState<SortKey>("new");
	const items = sortProducts(products, sort);

	// slug → { line, index } for label + palette resolution.
	const lineBySlug = useMemo(() => {
		const map = new Map<string, { line: Line; index: number }>();
		lines.forEach((line, index) => {
			map.set(line.slug, { line, index });
		});
		return map;
	}, [lines]);

	return (
		<div>
			<HairlineDivider className="mb-[18px]" />

			{/* Count + optional sort */}
			{(showCount || showSort) && (
				<div className="flex flex-wrap items-center justify-between gap-4 pb-[18px]">
					{showCount && (
						<span className="font-mono text-mono-sm uppercase tracking-[var(--tracking-mono)] text-muted">
							{items.length} {items.length === 1 ? "shirt" : "shirts"}
						</span>
					)}
					{showSort && (
						<div className="flex items-center gap-[18px]">
							<span className="font-mono text-mono-sm uppercase tracking-[var(--tracking-mono)] text-muted">
								Sort
							</span>
							<div className="flex items-center gap-[9px]">
								{SORTS.map(([key, label]) => (
									<button
										key={key}
										type="button"
										onClick={() => setSort(key)}
										aria-pressed={sort === key}
										className={cn(
											"border-b py-0.5 font-mono text-mono-sm uppercase tracking-[var(--tracking-mono)] transition-colors",
											sort === key
												? "border-accent text-ink"
												: "border-transparent text-muted hover:text-ink",
										)}
									>
										{label}
									</button>
								))}
							</div>
						</div>
					)}
				</div>
			)}

			{items.length === 0 ? (
				<div className="py-16 text-center">
					<span className="font-mono text-mono-md text-muted">
						{emptyMessage}
					</span>
				</div>
			) : (
				<div className="grid grid-cols-2 gap-6 md:grid-cols-3 xl:grid-cols-5">
					{items.map((product) => {
						const resolved = lineBySlug.get(product.line.slug);
						const lineLabel =
							showLine && resolved
								? `LINE ${String(resolved.index + 1).padStart(2, "0")} · ${product.line.name.toUpperCase()}`
								: undefined;
						return (
							<StyleProductCard
								key={product.slug}
								product={product}
								line={resolved?.line}
								lineLabel={lineLabel}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
}
